import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback
} from 'react';
import './SmartChatDashboard.scss';
import BaseTileLayout from '../../../Layouts/BaseTileLayout';
import { Card, TileLayout } from '@progress/kendo-react-layout';
import { Loader, buildNotification, GenerateNotification } from 'smart-react';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES
} from '../../../constants/eventDataTypes';
import {
  adjustDashboard,
  fetchTileContent,
  getDashboard,
  deleteTile as deleteSmartAppTile,
  editTile
} from '../Services/SmartChatDashboardService';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Input, TextArea } from '@progress/kendo-react-inputs';
import { Button, DropDownButton } from '@progress/kendo-react-buttons';
// Use lightweight tile content components (do not use shared FormatMessage ones)
import SimpleGrid from '../Components/TileContent/SimpleGrid';
import SimpleChartImage from '../Components/TileContent/SimpleChartImage';
import SimpleKendoChart from '../Components/TileContent/SimpleKendoChart';
import eventBus from '../../../Utils/Common/eventBus';
import { listAssistants } from '../../Assistant/Services/AiAssistantService';
import { ASSISTANT_TYPES } from '../../../constants/applicationConstants';

// Extract chart image bytes from execution_steps if present.
// Some backend responses return `type: 'Grid'` but include a later analysis step with a chart image.
const extractChartImageFromSteps = (executionSteps) => {
  const steps = Array.isArray(executionSteps) ? executionSteps : [];
  // Prefer the last step that contains an image payload
  const stepWithImg = steps
    .slice()
    .reverse()
    .find(
      s =>
        s?.result?.attachemnt?.content ||
        s?.result?.attachment?.content ||
        s?.result?.image?.content
    );
  if (!stepWithImg?.result) return null;
  const r = stepWithImg.result;
  const content =
    r?.attachemnt?.content || r?.attachment?.content || r?.image?.content;
  if (!content) return null;
  return {
    content,
    type: r?.attachemnt?.type || r?.attachment?.type || r?.image?.type || 'image/png',
    name: r?.attachemnt?.name || r?.attachment?.name || r?.image?.name || 'chart.png'
  };
};

const extractChartDataFromContent = (content) => {
  // Support multiple backend shapes:
  // 1) { payload: { chart_data } }
  // 2) { result: { payload: { chart_data } } }
  // 3) { chart_data }
  const cd =
    content?.payload?.chart_data ||
    content?.payload?.chartData ||
    content?.result?.payload?.chart_data ||
    content?.result?.payload?.chartData ||
    content?.chart_data ||
    null;
  if (!cd || typeof cd !== 'object') return null;
  return cd;
};

const extractChartDataFromSteps = (executionSteps) => {
  const steps = Array.isArray(executionSteps) ? executionSteps : [];
  // Prefer the second step (index 1) as per backend contract
  const second = steps[1]?.result;
  if (second) {
    const cd =
      second?.payload?.chart_data ||
      second?.payload?.chartData ||
      second?.chart_data ||
      second?.chartData ||
      null;
    if (cd && typeof cd === 'object') return cd;
  }
  // Fallback: scan from the end to find the most recent chart_data
  const stepWithChart = steps
    .slice()
    .reverse()
    .find(s => s?.result?.payload?.chart_data || s?.result?.payload?.chartData || s?.result?.chart_data || s?.result?.chartData);
  if (!stepWithChart?.result) return null;
  return extractChartDataFromContent(stepWithChart.result);
};

const isSupportedKendoChartType = (chartData) => {
  const inferredType = chartData?.lines?.[0]?.type || chartData?.Lines?.[0]?.Type;
  const t = String(chartData?.type || inferredType || '').toLowerCase();
  return t === 'pie' || t === 'bar' || t === 'line';
};

const TileHeader = ({ title, subtitle, onRefresh, onFullscreen, onDelete, onEdit, tile, disabled }) => {
  // Remove Refresh from dropdown; keep only Edit/Delete
  const items = [
    onEdit && { text: 'Edit', id: 'edit', icon: 'fas fa-pen' },
    onDelete && { text: 'Delete', id: 'delete', icon: 'fas fa-trash' }
  ].filter(Boolean);

  const onItemClick = (e) => {
    const actionId = e.item?.id || (e.item?.text ? e.item.text.toLowerCase() : '');
    switch (actionId) {
      case 'edit':
        return onEdit?.(tile);
      case 'delete':
        return onDelete?.(tile);
      default:
        return null;
    }
  };

  return (
    <div
      className="k-d-flex k-align-items-center k-px-2"
      style={{ minHeight: 32 }}
    >
      <div className="k-d-flex k-flex-col k-overflow-hidden">
        <div
          className="k-text-bold"
          style={{ fontSize: 14, lineHeight: '18px' }}
        >
          {title}
        </div>
        {subtitle && (
          <div
            className="k-text-secondary"
            style={{ fontSize: 12, lineHeight: '16px' }}
          >
            {subtitle}
          </div>
        )}
      </div>
      <div className="k-ml-auto k-d-flex k-align-items-center" style={{ gap: 4 }}>
        {onRefresh ? (
          <Button
            size="small"
            fillMode="flat"
            title="Refresh"
            aria-label="Refresh tile"
            onClick={() => onRefresh(tile)}
            disabled={disabled}
            rounded="full"
            style={{ minWidth: 20, width: 20, height: 20, padding: 0 }}
          >
            <span className="k-icon k-font-icon k-i-reload" style={{ fontSize: 12 }} />
          </Button>
        ) : null}
        {onFullscreen ? (
          <Button
            size="medium"
            fillMode="flat"
            title="Fullscreen"
            aria-label="Fullscreen"
            onClick={() => onFullscreen(tile)}
            disabled={disabled}
            rounded="full"
            style={{ minWidth: 28, width: 28, height: 28, padding: 0 }}
          >
            <span className="k-icon k-font-icon k-i-maximize" style={{ fontSize: 16 }} />
          </Button>
        ) : null}
        {/* Single actions dropdown (ellipsis aligned to right) */}
        <DropDownButton
          items={items}
          onItemClick={onItemClick}
          fillMode="flat"
          size="small"
          aria-label="Tile actions"
          title="Tile actions"
          popupSettings={{
            animate: true,
            popupClass: 'tile-actions-popup',
            // Render to body to avoid clipping by overflow/positioned parents
            appendTo: () => (typeof document !== 'undefined' ? document.body : undefined),
            anchorAlign: { horizontal: 'right', vertical: 'bottom' },
            popupAlign: { horizontal: 'right', vertical: 'top' }
          }}
          iconClass="fas fa-ellipsis-v fa-fw"
          disabled={disabled}
        />
      </div>
    </div>
  );
};

const SmartChatDashboard = ({ refreshToken = 0, appKeys: propAppKeys = null }) => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [tiles, setTiles] = useState([]);
  const [tileContents, setTileContents] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editContext, setEditContext] = useState(null); // { tile, name, desc }
  const [savingEdit, setSavingEdit] = useState(false);
  const saveTimerRef = useRef(null);
  // Keep a snapshot of tiles before entering edit mode so Cancel can restore without flicker
  const preEditTilesRef = useRef(null);
  // Layout edit mode (enable drag/resize, persist only on explicit Save)
  const [editMode, setEditMode] = useState(false);
  const [layoutDirty, setLayoutDirty] = useState(false);
  // Track tiles being deleted to show per-tile loaders and disable actions
  const [deletingTileIds, setDeletingTileIds] = useState(new Set());
  // Track tiles being refreshed to show per-tile loaders and disable actions
  const [refreshingTileIds, setRefreshingTileIds] = useState(new Set());
  // Fullscreen overlay context { tile, content }
  const [fullscreenCtx, setFullscreenCtx] = useState(null);
  // Primary SmartApps AppKey associated with the canonical dashboard used for layout saves
  const [primaryAppKey, setPrimaryAppKey] = useState(null);
  // --- Responsive TileLayout columns ---
  // The server/dashboard is designed around 6 columns, but the UI should reduce columns on smaller viewports
  // to avoid tiles getting “squeezed”.
  const BASE_COLUMNS = 6;
  const [layoutColumns, setLayoutColumns] = useState(BASE_COLUMNS);
  const [tileLayoutEl, setTileLayoutEl] = useState(null);

  // Allow tiles to shrink more aggressively (aligns with analytics demo behavior)
  const MIN_COL_SPAN = 1;
  const MIN_ROW_SPAN = 1;

  const clampItem = it => ({
    ...it,
    // Kendo TileLayout behaves poorly if colSpan exceeds the declared columns.
    // Clamp to [1..BASE_COLUMNS] (server grid) to ensure widths we persist are always valid.
    colSpan: Math.min(
      BASE_COLUMNS,
      Math.max(MIN_COL_SPAN, it.colSpan ?? MIN_COL_SPAN)
    ),
    rowSpan: Math.max(MIN_ROW_SPAN, it.rowSpan ?? MIN_ROW_SPAN)
  });

  /**
   * Pack tiles top-to-bottom / left-to-right into a grid so every tile always has `col` and `row`.
   * TileLayout requires `col` (and behaves best with `row`) for resizing/reordering in controlled mode.
   */
  const packTiles = useCallback(
    (items, columns) => {
      const COLS = Math.max(1, Number(columns) || 1);
      const sorted = [...(items || [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0)
      );
      const occupied = []; // occupied[rowIdx][colIdx] => boolean (0-based)

      const ensureRows = count => {
        while (occupied.length < count) {
          occupied.push(new Array(COLS).fill(false));
        }
      };

      const canPlace = (row, col, colSpan, rowSpan) => {
        // row/col are 1-based
        const r0 = row - 1;
        const c0 = col - 1;
        if (col < 1 || col > COLS) return false;
        if (colSpan < 1 || rowSpan < 1) return false;
        if (c0 + colSpan > COLS) return false;
        ensureRows(r0 + rowSpan);
        for (let r = r0; r < r0 + rowSpan; r += 1) {
          for (let c = c0; c < c0 + colSpan; c += 1) {
            if (occupied[r][c]) return false;
          }
        }
        return true;
      };

      const mark = (row, col, colSpan, rowSpan) => {
        const r0 = row - 1;
        const c0 = col - 1;
        ensureRows(r0 + rowSpan);
        for (let r = r0; r < r0 + rowSpan; r += 1) {
          for (let c = c0; c < c0 + colSpan; c += 1) {
            occupied[r][c] = true;
          }
        }
      };

      const result = [];
      for (const it of sorted) {
        // Keep the *stored* colSpan as the "base" (persistable) span,
        // but place/render with a span clamped to the current viewport columns.
        const baseColSpan = Math.min(
          BASE_COLUMNS,
          Math.max(1, Number(it.colSpan) || 1)
        );
        const effectiveColSpan = Math.min(COLS, baseColSpan);
        const rowSpan = Math.max(1, Number(it.rowSpan) || 1);

        let placed = false;
        for (let row = 1; !placed; row += 1) {
          for (let col = 1; col <= COLS; col += 1) {
            if (canPlace(row, col, effectiveColSpan, rowSpan)) {
              mark(row, col, effectiveColSpan, rowSpan);
              // Preserve base colSpan/rowSpan in state; only col/row depend on viewport columns
              result.push({ ...it, col, row, colSpan: baseColSpan, rowSpan });
              placed = true;
              break;
            }
          }
        }
      }
      return result;
    },
    [BASE_COLUMNS]
  );

  const getColumnsForWidth = width => {
    const w = Number(width) || 0;
    // Tune these breakpoints as needed.
    if (w >= 1200) return 6;
    if (w >= 900) return 4;
    if (w >= 600) return 2;
    return 1;
  };

  // Observe container width to adjust TileLayout columns (avoid 0-width when hidden)
  useEffect(() => {
    const el = tileLayoutEl;
    if (!el) return undefined;

    const measure = () => {
      if (editMode) return; // do not fight user during edit mode
      const rect = el.getBoundingClientRect();
      const width = rect?.width && rect.width > 0 ? rect.width : (typeof window !== 'undefined' ? window.innerWidth : 1200);
      const nextCols = Math.min(BASE_COLUMNS, getColumnsForWidth(width));
      setLayoutColumns(prev => (prev === nextCols ? prev : nextCols));
    };

    // initial measurement (handles hidden/visibility change)
    measure();

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }
    window.addEventListener('resize', measure);

    // Defer one more measurement after paint to handle visibility transitions
    const t = setTimeout(measure, 200);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
      clearTimeout(t);
    };
  }, [BASE_COLUMNS, tileLayoutEl, editMode]);

  // Re-pack tiles whenever the viewport column count changes so `col` is always valid.
  useEffect(() => {
    // Avoid repacking while editing layout; it would fight user positioning/resizing.
    if (editMode) return;
    setTiles(prev => packTiles(prev.map(clampItem), layoutColumns));
    // NOTE: We intentionally do NOT persist layout here — resizing the browser should not save to backend.
  }, [layoutColumns, packTiles, editMode]);

  // Normalize grid values to a 2D array aligned with metadata headings
  const normalizeGridValues = (metadata = [], values) => {
    // If already an array of arrays, return as-is
    if (
      Array.isArray(values) &&
      values.length > 0 &&
      Array.isArray(values[0])
    ) {
      return values;
    }

    // If array of objects, map object fields to metadata order
    if (
      Array.isArray(values) &&
      values.length > 0 &&
      typeof values[0] === 'object' &&
      !Array.isArray(values[0])
    ) {
      const headings = Array.isArray(metadata)
        ? metadata.map(h =>
            typeof h === 'string'
              ? h
              : h?.column || h?.display_name || h?.field || ''
          )
        : [];
      return values.map(rowObj =>
        headings.map(h => {
          const key = h || '';
          // try several common keys
          return (
            rowObj?.[key] ??
            rowObj?.[key?.toLowerCase?.()] ??
            rowObj?.[key?.replace(/\s+/g, '_')] ??
            null
          );
        })
      );
    }

    // Otherwise, if no data, return empty
    return Array.isArray(values) ? values : [];
  };

  // Handlers (follow app style: define named functions instead of inlining logic in render)
  // Helper: get current session key
  const getSessionKey = () =>
    (typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem('SMARTCHAT_SESSION_KEY')
      : '') || '';

  // Helper: compose initial tile content from dashboard metadata
  const buildInitialContentFromMetadata = items => {
    const initialEntries = items.map(it => {
      const d = it.data || {};
      const type = d.Type || 'Grid';
      let mObj = null;
      try {
        if (d.Metadata) {
          const parsed =
            typeof d.Metadata === 'string'
              ? JSON.parse(d.Metadata)
              : d.Metadata;
          mObj = parsed || null;
        }
      } catch (_) {}

      if (type === 'Grid') {
        const metadata = Array.isArray(mObj) ? mObj : mObj?.metadata || [];
        // If values are included under metadata.values, prefer them; otherwise seed with a single empty row
        const metaValsRaw = Array.isArray(mObj?.values)
          ? mObj.values
          : Array.isArray(mObj?.Values)
            ? mObj.Values
            : [];
        const normalized = normalizeGridValues(metadata, metaValsRaw);
        const finalVals =
          normalized && normalized.length > 0
            ? normalized
            : metadata && metadata.length > 0
              ? [new Array(metadata.length).fill(null)]
              : [];
        return [
          it.id,
          {
            type: 'Grid',
            payload: { metadata, values: finalVals },
            // Explicit message so Grid tiles show a loading state instead of
            // the Kendo default "No records available" while data is loading.
            message: 'Loading records…'
          }
        ];
      }
      if (type === 'Chart') {
        // Prefer structured chart_data (for pie/line/bar) so we can render a real Kendo chart
        if (mObj?.chart_data) {
          return [
            it.id,
            {
              type: 'Chart',
              payload: { chart_data: mObj.chart_data },
              message: d?.Description || 'Chart'
            }
          ];
        }

        // Fallback: If chart bytes are present in metadata.attachemnt, seed with that
        const attFromMeta = mObj?.attachemnt || mObj?.attachment;
        if (attFromMeta?.content) {
          return [
            it.id,
            {
              type: 'Chart',
              attachemnt: {
                content: attFromMeta.content,
                type: attFromMeta.type || 'image/png',
                name: attFromMeta.name || 'chart.png'
              },
              message: 'Loading…'
            }
          ];
        }
        return [it.id, { type: 'Chart', message: 'Loading…' }];
      }
      return [it.id, { type: 'Tile', message: 'Loading…' }];
    });
    return Object.fromEntries(initialEntries);
  };

  // Refresh live WMS content for the given items.
  // Best practice: update each tile as soon as its content returns,
  // instead of waiting for *all* tiles to finish.
  const refreshTileContent = async items => {
    await Promise.all(
      items.map(async it => {
        try {
          const apiKey = it?.data?.ApiKey || it?.data?.apiKey || '';
          const sessionKey = getSessionKey();
          let content = await fetchTileContent(it.id, { apiKey, sessionKey });

          // Only accept usable content; otherwise keep previously seeded data
          const isUsable = (() => {
            if (!content) return false;
            const statusOk =
              content?.status_code == null ||
              (content?.status_code >= 200 && content?.status_code < 400);
            if (content?.type === 'Chart') {
              const hasDirectImage = !!(
                content?.attachemnt?.content || content?.attachment?.content
              );
              const hasResultImage = Array.isArray(content?.payload?.execution_steps)
                ? content.payload.execution_steps.some(
                    s =>
                      s?.result?.attachemnt?.content ||
                      s?.result?.attachment?.content ||
                      s?.result?.image?.content
                  )
                : false;
              const directChartData = extractChartDataFromContent(content);
              const stepChartData = extractChartDataFromSteps(
                content?.payload?.execution_steps
              );
              const hasChartData = !!directChartData || !!stepChartData;
              return statusOk && (hasDirectImage || hasResultImage || hasChartData);
            }
            if (content?.type === 'Grid') {
              const stepChartData = extractChartDataFromSteps(
                content?.payload?.execution_steps
              );
              return (
                statusOk &&
                !!(
                  content?.payload?.metadata ||
                  content?.payload?.values ||
                  content?.Metadata ||
                  content?.Values ||
                  stepChartData ||
                  (Array.isArray(content?.payload?.execution_steps) &&
                    content?.payload?.execution_steps?.some(
                      s => s?.result?.metadata || s?.result?.values
                    ))
                )
              );
            }
            // For unknown types, require success code and message
            return statusOk && !!content?.message;
          })();
          if (!isUsable) return;

          // If there is structured chart_data (pie/bar), prefer it so dashboard shows real Kendo chart.
          // This can come either from Chart tile responses or from execution_steps results.
          const directChartData = extractChartDataFromContent(content);
          if (directChartData && isSupportedKendoChartType(directChartData)) {
            setTileContents(prev => ({
              ...prev,
              [it.id]: {
                type: 'Chart',
                payload: { chart_data: directChartData }
              }
            }));
            return;
          }

          const stepChartData = extractChartDataFromSteps(
            content?.payload?.execution_steps
          );
          if (stepChartData && isSupportedKendoChartType(stepChartData)) {
            setTileContents(prev => ({
              ...prev,
              [it.id]: {
                type: 'Chart',
                payload: { chart_data: stepChartData }
              }
            }));
            return;
          }

          // Otherwise, if execution_steps contains a chart image, show the image.
          // This handles "Grid" tile responses that include a later analysis step with a chart PNG.
          const chartFromSteps = extractChartImageFromSteps(
            content?.payload?.execution_steps
          );
          if (chartFromSteps?.content) {
            const normalized = {
              type: 'Chart',
              attachemnt: {
                content: chartFromSteps.content,
                type: chartFromSteps.type,
                name: chartFromSteps.name
              }
            };
            setTileContents(prev => ({ ...prev, [it.id]: normalized }));
            return;
          }

          // Always derive final content from the latest execution_steps[].result
          if (content?.type === 'Grid') {
            const steps = Array.isArray(content?.payload?.execution_steps)
              ? content.payload.execution_steps
              : [];
            // prefer the last step that contains a result
            const stepWithResult = steps
              .slice()
              .reverse()
              .find(s => s?.result && (s.result.metadata || s.result.values));

            let md = [];
            let vals;
            if (stepWithResult?.result) {
              md = stepWithResult.result.metadata || [];
              vals = stepWithResult.result.values;
            } else {
              // fallback to top-level if no steps present
              md = content?.payload?.metadata || content?.Metadata || [];
              vals = content?.payload?.values || content?.Values;
            }

            const normalizedVals = normalizeGridValues(md, vals);
            const normalized = {
              type: 'Grid',
              payload: {
                metadata: Array.isArray(md) ? md : [],
                values: Array.isArray(normalizedVals) ? normalizedVals : []
              }
            };
            setTileContents(prev => ({ ...prev, [it.id]: normalized }));
          } else if (content?.type === 'Chart') {
            // Prefer structured chart data if present
            const cd = extractChartDataFromContent(content);
            if (cd && isSupportedKendoChartType(cd)) {
              setTileContents(prev => ({
                ...prev,
                [it.id]: {
                  type: 'Chart',
                  payload: { chart_data: cd }
                }
              }));
              return;
            }

            const steps = Array.isArray(content?.payload?.execution_steps)
              ? content.payload.execution_steps
              : [];
            // prefer the last step that contains an image result
            const stepWithImg = steps
              .slice()
              .reverse()
              .find(
                s =>
                  s?.result?.attachemnt?.content ||
                  s?.result?.attachment?.content ||
                  s?.result?.image?.content
              );
            let imgContent =
              content?.attachemnt?.content ||
              content?.attachment?.content ||
              null;
            let imgType =
              content?.attachemnt?.type ||
              content?.attachment?.type ||
              'image/png';
            let imgName =
              content?.attachemnt?.name ||
              content?.attachment?.name ||
              'chart.png';

            if (stepWithImg?.result) {
              const r = stepWithImg.result;
              imgContent = r?.attachemnt?.content || r?.attachment?.content || r?.image?.content || imgContent;
              imgType = r?.attachemnt?.type || r?.attachment?.type || r?.image?.type || imgType;
              imgName = r?.attachemnt?.name || r?.attachment?.name || r?.image?.name || imgName;
            }

            const normalized = {
              type: 'Chart',
              attachemnt: imgContent
                ? { content: imgContent, type: imgType, name: imgName }
                : undefined
            };
            setTileContents(prev => ({ ...prev, [it.id]: normalized }));
          }
        } catch {
          // ignore individual tile errors; existing content (if any) stays
        }
      })
    );
  };

  const handleRefreshTile = useCallback(async (tile) => {
    try {
      // Resolve full tile from state if needed
      const fullTile = tile?.data ? tile : tiles.find(t => t.id === tile?.id) || tile;
      const tileId = fullTile?.data?.TileId || fullTile?.id;
      if (!tileId) return;
      setRefreshingTileIds(prev => {
        const next = new Set(prev);
        next.add(`${tileId}`);
        return next;
      });
      await refreshTileContent([fullTile]);
    } finally {
      const fullTile = tile?.data ? tile : tiles.find(t => t.id === tile?.id) || tile;
      const tileId = fullTile?.data?.TileId || fullTile?.id;
      if (tileId) {
        setRefreshingTileIds(prev => {
          const next = new Set(prev);
          next.delete(`${tileId}`);
          return next;
        });
      }
    }
  }, [tiles]);

  const handleOpenFullscreen = useCallback((tile) => {
    const fullTile = tile?.data ? tile : tiles.find(t => t.id === tile?.id) || tile;
    if (!fullTile?.id) return;
    const content = tileContents?.[fullTile.id];
    setFullscreenCtx({ tile: fullTile, content });
  }, [tiles, tileContents]);

  const handleCloseFullscreen = useCallback(() => setFullscreenCtx(null), []);

  // Fetch tiles from Smart Apps API for **all assistants' SmartApps AppKeys**.
  // We now rely entirely on the SmartApps/assistant server for tile storage
  // and visibility (no separate Laravel/user-dashboard-tiles mapping).
  const hasLoadedTilesRef = useRef(false);
  const fetchTiles = async () => {
    setLoading(true);
    try {
      // Discover all assistant-specific SmartApps AppKeys (sa_tenant_key).
      // Prefer keys passed from SmartChat (so we don't call assistants API twice).
      let appKeys = [];
      if (Array.isArray(propAppKeys) && propAppKeys.length > 0) {
        // Ensure uniqueness and filter out empty values
        appKeys = Array.from(new Set(propAppKeys.filter(Boolean)));
      } else {
        try {
          const asstResp = await listAssistants(-1);
          if (asstResp?.isSuccess && Array.isArray(asstResp.data)) {
            const keySet = new Set();
            asstResp.data.forEach(a => {
              if (
                a.type === ASSISTANT_TYPES.BLUEYONDER ||
                a.type === ASSISTANT_TYPES.SMARTAPPS ||
                a.type === ASSISTANT_TYPES.ENTERPRISE
              ) {
                try {
                  const parsed = a.data ? JSON.parse(a.data) : null;
                  const k = parsed?.sa_tenant_key;
                  if (k) keySet.add(k);
                } catch (_) {
                  // ignore malformed assistant data
                }
              }
            });
            appKeys = Array.from(keySet);
          }
        } catch (e) {
          console.warn('Failed to load assistants for dashboard SmartApps AppKeys:', e);
        }
      }

      let allTiles = [];
      let dashboardPayload = null;
      let dashboardAppKey = null;

      if (appKeys.length > 0) {
        // 3a) Multi-appkey mode: call SmartApps dashboard for each assistant AppKey
        const dashboardResponses = await Promise.all(
          appKeys.map(async (key) => {
            try {
              const resp = await getDashboard(null, key);
              return { resp, appKey: key };
            } catch (e) {
              console.warn('Failed to load dashboard for appKey', key, e);
              return { resp: null, appKey: key };
            }
          })
        );

        const successful = dashboardResponses.filter(
          x => x.resp?.isSuccess && Array.isArray(x.resp.payload?.Tiles)
        );

        if (successful.length === 0) {
          GenerateNotification(
            buildNotification({
              title: 'Failed to load dashboard',
              description: 'No SmartApps dashboards could be loaded for any assistant AppKey.',
              style: 'error'
            }),
            NOTIFICATION_TYPES.APP,
            EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
          );
          return;
        }

        // Choose a primary dashboard (prefer one that actually has tiles)
        const primary =
          successful.find(x => (x.resp.payload?.Tiles || []).length > 0) ||
          successful[0];

        dashboardPayload = primary.resp.payload;
        dashboardAppKey = primary.appKey;
        // Build combined tiles list with primary dashboard tiles first,
        // then tiles from other dashboards. This lets us de-duplicate by
        // TileId while preferring the canonical/primary dashboard's
        // definition when the same tile appears under multiple SmartApps
        // AppKeys for the same user.
        const primaryTiles = (primary.resp.payload?.Tiles || []).map(t => ({
          ...t,
          SmartAppKey: primary.appKey,
        }));

        const otherTiles = successful
          .filter(x => x.appKey !== primary.appKey)
          .flatMap(({ resp, appKey }) =>
            (resp.payload?.Tiles || []).map(t => ({
              ...t,
              SmartAppKey: appKey,
            }))
          );

        const combinedTiles = [...primaryTiles, ...otherTiles];

        // De-duplicate tiles by TileId so the same SmartApps tile is not
        // rendered multiple times when a user has more than one AppKey
        // that maps to the same dashboard.
        const seenTileIds = new Set();
        allTiles = combinedTiles.filter(t => {
          const tileId = t.TileId ?? t.id;
          if (!tileId) return true;
          if (seenTileIds.has(tileId)) return false;
          seenTileIds.add(tileId);
          return true;
        });
      } else {
        // 3b) Fallback: single env-based dashboard (backwards compatibility)
        const resp = await getDashboard();
        if (!resp?.isSuccess) {
          GenerateNotification(
            buildNotification({
              title: 'Failed to load dashboard',
              description: resp?.message || 'Unknown error',
              style: 'error'
            }),
            NOTIFICATION_TYPES.APP,
            EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
          );
          return;
        }
        dashboardPayload = resp.payload;
        dashboardAppKey = null;
        allTiles = (resp.payload?.Tiles || []).map(t => ({ ...t, SmartAppKey: null }));
      }

      // Save canonical dashboard + primary app key used for layout saves
      setDashboard(dashboardPayload);
      setPrimaryAppKey(dashboardAppKey);

      // Build tile items directly from SmartApps dashboard tiles
      const items = allTiles
        .map((t, i) => {
          // Ignore server-provided row/col to avoid large empty gaps and allow dense placement from top
          // Keep spans and an explicit order so Kendo auto-places tiles densely (autoFlow="row dense")
          let w = Number.isFinite(+t?.Width) ? +t.Width : MIN_COL_SPAN; // Width -> colSpan
          let h = Number.isFinite(+t?.Height) ? +t.Height : MIN_ROW_SPAN; // Height -> rowSpan

          // When a tile is first added, SmartApps may send a very large default (e.g. Width=6 Height=6).
          // This makes tiles appear oversized and can break horizontal resize behavior.
          // Normalize to a reasonable default size per tile type.
          const tileType = t?.Type || 'Grid';
          const isHugeDefault = w === BASE_COLUMNS && h === BASE_COLUMNS;
          if (isHugeDefault) {
            if (tileType === 'Grid') {
              w = Math.min(BASE_COLUMNS, 4);
              h = 3;
            } else if (tileType === 'Chart') {
              w = Math.min(BASE_COLUMNS, 3);
              h = 4;
            } else {
              w = Math.min(BASE_COLUMNS, 3);
              h = 2;
            }
          }

          // Ensure charts have enough vertical space so labels / axes are readable.
          if (tileType === 'Chart') {
            h = Math.max(h, 4);
          }

          return {
            id: t.TileId,
            order: i,
            // col/row will be calculated via packTiles() so controlled resizing works reliably
            colSpan: Math.max(MIN_COL_SPAN, w),
            rowSpan: Math.max(MIN_ROW_SPAN, h),
            data: t,
            resizable: true,
            reorderable: true
          };
        })
        .map(clampItem);

      setTiles(packTiles(items, layoutColumns));


      // Seed UI with metadata so tiles render immediately
      const seeded = buildInitialContentFromMetadata(items);
      setTileContents(prev => ({ ...prev, ...seeded }));

      // After first paint, silently fetch latest results and update state
      // IMPORTANT: this is the heavy part (1 request per tile). It's intentionally delayed.
      if (typeof window !== 'undefined') {
        window.requestAnimationFrame?.(() =>
          setTimeout(() => refreshTileContent(items), 200)
        );
      } else {
        setTimeout(() => refreshTileContent(items), 200);
      }
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteTile = useCallback(
    async tile => {
      try {
        console.log(tile);
        // Fallback to state if tile.data not present
        const fullTile = tile?.data
          ? tile
          : tiles.find(t => t.id === tile?.id) || tile;
        const tileId = fullTile?.data?.TileId || fullTile?.id;
        if (!tileId) {
          GenerateNotification(
            buildNotification({
              title: 'Missing Tile Id',
              description: 'Unable to resolve the tile identifier.',
              style: 'warning'
            }),
            NOTIFICATION_TYPES.APP,
            EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
          );
          return;
        }
        // Mark tile as deleting to show loader
        setDeletingTileIds(prev => {
          const next = new Set(prev);
          next.add(`${tileId}`);
          return next;
        });

        const clearDeleting = () =>
          setDeletingTileIds(prev => {
            const next = new Set(prev);
            next.delete(`${tileId}`);
            return next;
          });

        // Delete from Smart Apps (use tile-specific SmartApps AppKey when available)
        const appResp = await deleteSmartAppTile(
          tileId,
          fullTile?.data?.SmartAppKey || primaryAppKey
        );
        if (!appResp?.isSuccess) {
          GenerateNotification(
            buildNotification({
              title: 'Delete failed (SmartApp)',
              description:
                appResp?.message || 'Unable to delete from Smart Apps',
              style: 'error'
            }),
            NOTIFICATION_TYPES.APP,
            EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
          );
          clearDeleting();
          return;
        }

        // Update UI when SmartApp delete succeeds
        setTiles(prev => {
          const updated = prev.filter(p => p.id !== (fullTile?.id || tileId));
          // Re-pack remaining tiles to immediately close any visual gaps
          const packed = packTiles(updated.map(clampItem), layoutColumns);
          schedulePersist(packed);
          return packed;
        });
        clearDeleting();
      } catch (e) {
        GenerateNotification(
          buildNotification({
            title: 'Error',
            description: e.message,
            style: 'error'
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
        );
        // Ensure loader clears on error
        try {
          const fullTile = tile?.data
            ? tile
            : tiles.find(t => t.id === tile?.id) || tile;
          const tileId = fullTile?.data?.TileId || fullTile?.id;
          if (tileId) {
            setDeletingTileIds(prev => {
              const next = new Set(prev);
              next.delete(`${tileId}`);
              return next;
            });
          }
        } catch (_) {}
      }
    },
    [tiles, primaryAppKey, packTiles, layoutColumns]
  );

  const handleOpenEdit = useCallback(
    tile => {
      // Resolve full tile if a minimal shape was passed from UI layer
      const fullTile = tile?.data
        ? tile
        : tiles.find(t => t.id === tile?.id) || tile;
      setEditContext({
        tile: fullTile,
        name: fullTile?.data?.Name || '',
        desc: fullTile?.data?.Description || ''
      });
      setEditDialogOpen(true);
    },
    [tiles]
  );

  useEffect(() => {
    if (hasLoadedTilesRef.current) return; // StrictMode-safe: ensure single initial load
    hasLoadedTilesRef.current = true;
    fetchTiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Allow parent (SmartChat overlay) to force a refresh when a tile is added.
  // We intentionally ignore `prefetched` here so we always fetch the latest state.
  useEffect(() => {
    if (!refreshToken) return;
    fetchTiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // Refresh tiles when a new tile is added from chat modal.
  // Also seed content for the newly added tile using the payload/chartSeed
  // passed from AddToDashboardModal so we don't wait on refreshTileContent
  // just to show the initial chart.
  useEffect(() => {
    const handler = (evt) => {
      // If the event includes the tile payload and/or chart seed, patch
      // the tileContents cache immediately so the new tile shows a chart
      // completes.
      try {
        const { tileId, tile, chartSeed } = evt || {};
        if (!tileId) return;
        setTileContents(prev => {
          const existing = prev[tileId];
          // If we already have content, don't override.
          if (existing) return prev;

          // Prefer structured chart_data if provided by chartSeed
          if (chartSeed && Object.keys(chartSeed || {}).length > 0) {
            return {
              ...prev,
              [tileId]: {
                type: 'Chart',
                payload: { chart_data: chartSeed }
              }
            };
          }

          // Else, if backend tile payload already includes Metadata (for Grid)
          // or any minimal structure, seed a basic placeholder.
          if (tile && tile.Metadata) {
            let mObj = null;
            try {
              mObj = typeof tile.Metadata === 'string'
                ? JSON.parse(tile.Metadata)
                : tile.Metadata;
            } catch (_) {}
            const metadata = Array.isArray(mObj) ? mObj : mObj?.metadata || [];
            return {
              ...prev,
              [tileId]: {
                type: tile.Type || 'Grid',
                payload: { metadata, values: [] },
                // Show a consistent hint while the dataset loads
                message: 'Loading records…'
              }
            };
          }

          return prev;
        });
      } catch (_) {}
    };
    eventBus.on('DASHBOARD_TILE_ADDED', handler);
    return () => {
      try { eventBus.remove('DASHBOARD_TILE_ADDED', handler); } catch (e) {}
    };
  }, []);

  // Auto-exit edit mode if all tiles are removed to avoid showing edit banner/actions
  useEffect(() => {
    if (editMode && tiles.length === 0) {
      setEditMode(false);
      setLayoutDirty(false);
    }
  }, [tiles, editMode]);

  const schedulePersist = newItems => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      if (!dashboard?.DashboardId) return;
      const payloadTiles = newItems.map((it, idx) => {
        const prev = tiles.find(p => p.id === it.id) || tiles[idx] || {};
        const order = Number.isFinite(it.order)
          ? it.order
          : Number.isFinite(prev.order)
            ? prev.order
            : idx;
        const width = Number.isFinite(it.colSpan)
          ? it.colSpan
          : Number.isFinite(prev.colSpan)
            ? prev.colSpan
            : MIN_COL_SPAN;
        const height = Number.isFinite(it.rowSpan)
          ? it.rowSpan
          : Number.isFinite(prev.rowSpan)
            ? prev.rowSpan
            : MIN_ROW_SPAN;
        return {
          TileId: (it.data && it.data.TileId) || it.id,
          // Server expects Y as order (not axis). Persist order only; ignore absolute X/row/col.
          X: 0,
          Y: Math.max(0, order),
          Width: width,
          Height: height
        };
      });
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('Persisting tiles (Y=order only)', payloadTiles);
      }
      const resp = await adjustDashboard(
        dashboard.DashboardId,
        payloadTiles,
        primaryAppKey
      );
      if (!resp?.isSuccess) {
        GenerateNotification(
          buildNotification({
            title: 'Failed to save layout',
            description: resp?.message,
            style: 'warning'
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
        );
      }
    }, 400);
  };

  // Persist immediately on explicit Save button
  const persistNow = async items => {
    if (!dashboard?.DashboardId) return false;
    const payloadTiles = items.map((it, idx) => {
      const prev = tiles.find(p => p.id === it.id) || tiles[idx] || {};
      const order = Number.isFinite(it.order)
        ? it.order
        : Number.isFinite(prev.order)
          ? prev.order
          : idx;
      const width = Number.isFinite(it.colSpan)
        ? it.colSpan
        : Number.isFinite(prev.colSpan)
          ? prev.colSpan
          : MIN_COL_SPAN;
      const height = Number.isFinite(it.rowSpan)
        ? it.rowSpan
        : Number.isFinite(prev.rowSpan)
          ? prev.rowSpan
          : MIN_ROW_SPAN;
      return {
        TileId: (it.data && it.data.TileId) || it.id,
        X: 0,
        Y: Math.max(0, order),
        Width: width,
        Height: height
      };
    });
    const resp = await adjustDashboard(
      dashboard.DashboardId,
      payloadTiles,
      primaryAppKey
    );
    if (resp?.isSuccess) {
      GenerateNotification(
        buildNotification({
          title: 'Layout saved',
          description: 'Your dashboard layout has been updated.',
          style: 'success'
        }),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
      );
      return true;
    }
    GenerateNotification(
      buildNotification({
        title: 'Save failed',
        description: resp?.message || 'Unable to save layout',
        style: 'error'
      }),
      NOTIFICATION_TYPES.APP,
      EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
    );
    return false;
  };

  const mergeWithExistingData = (items, options = {}) =>
    items.map((it, idx) => {
      const prev = tiles.find(p => p.id === it.id) || tiles[idx] || {};
      // Preserve spans and adopt col/row if provided by Kendo (so width/position changes stick)
      const nextColSpan = Number.isFinite(+it.colSpan)
        ? Math.min(BASE_COLUMNS, Math.max(MIN_COL_SPAN, +it.colSpan))
        : Math.min(
            BASE_COLUMNS,
            Math.max(
              MIN_COL_SPAN,
              Number.isFinite(+prev.colSpan) ? +prev.colSpan : MIN_COL_SPAN
            )
          );
      const nextRowSpan = Number.isFinite(+it.rowSpan)
        ? Math.max(MIN_ROW_SPAN, +it.rowSpan)
        : Math.max(
            MIN_ROW_SPAN,
            Number.isFinite(+prev.rowSpan) ? +prev.rowSpan : MIN_ROW_SPAN
          );
      const merged = {
        id: it.id ?? prev.id,
        order: it.order ?? prev.order ?? idx,
        colSpan: nextColSpan,
        rowSpan: nextRowSpan,
        resizable: true,
        reorderable: true,
        data: prev.data || it.data
      };
      // Always adopt Kendo-provided col/row to avoid visual overlap; this mirrors the demo behavior
      if (Number.isFinite(+it.col)) merged.col = Math.max(1, +it.col);
      if (Number.isFinite(+it.row)) merged.row = Math.max(1, +it.row);
      return merged;
    });

  const onReposition = useCallback(
    e => {
      const newItems = mergeWithExistingData(e.value);
      newItems.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      if (editMode) {
        const packed = packTiles(newItems.map(clampItem), layoutColumns);
        setTiles(packed);
        setLayoutDirty(true);
      } else {
        setTiles(newItems);
        schedulePersist(newItems);
      }
    },
    [tiles, dashboard, editMode, packTiles, layoutColumns]
  );

  const onResize = useCallback(
    e => {
      const newItems = mergeWithExistingData(e.value);
      newItems.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      if (editMode) {
        // In edit mode, immediately pack to fill any freed space.
        const packed = packTiles(newItems.map(clampItem), layoutColumns);
        setTiles(packed);
        setLayoutDirty(true);
      } else {
        setTiles(newItems);
        schedulePersist(newItems);
      }
    },
    [tiles, dashboard, editMode, packTiles, layoutColumns]
  );

  const renderTileContent = (tile, contentOverride) => {
    const data = tiles.find(t => t.id === tile.id)?.data;
    const content = contentOverride ?? tileContents[tile.id];

    if (!content) return <div className="k-text-secondary">No content</div>;

    // Normalize tile type so backend transitional states (e.g. "Mid" for
    // in-progress charts) are treated as their final visual type. This keeps
    // loading states and layout (tile-body--chart vs tile-body--grid) consistent.
    const rawType = content?.type || data?.Type;
    const lowerType = String(rawType || '').toLowerCase();
    const type = lowerType === 'mid' ? 'Chart' : rawType;

    if (type === 'Grid') {
      // Prefer normalized fields if present
      let md = content?.payload?.metadata || content?.Metadata;
      let vals = content?.payload?.values || content?.Values;

      // Fallback: extract from payload.execution_steps[].result
      if ((!Array.isArray(md) || md.length === 0) && Array.isArray(content?.payload?.execution_steps)) {
        const stepWithResult = content.payload.execution_steps.find(
          s => s?.result && (s.result.metadata || s.result.values)
        );
        if (stepWithResult?.result) {
          md = stepWithResult.result.metadata || [];
          vals = stepWithResult.result.values;
        }
      }

      md = Array.isArray(md) ? md : [];
      // Normalize to 2D array against metadata order
      vals = normalizeGridValues(md, vals);
      if (!Array.isArray(vals) || vals.length === 0) {
        vals = md && md.length > 0 ? [new Array(md.length).fill(null)] : [];
      }
      // Pass through any message from tile content so SimpleGrid can show
      // a loading/no-records hint instead of the default Kendo text.
      // Use height="fill" so the grid (and its loading state) can fill the
      // available tile height. This lets the shared .tile-loading-message
      // class vertically center "Loading records…" the same way chart tiles
      return (
        <SimpleGrid
          metadata={md}
          values={vals}
          message={content?.message}
          height="fill"
        />
      );
    }
    if (type === 'Chart') {
      const chartData =
        content?.payload?.chart_data ||
        content?.payload?.chartData ||
        content?.chart_data ||
        null;
      const inferredType = chartData?.lines?.[0]?.type || chartData?.Lines?.[0]?.Type;
      const chartType = String(chartData?.type || inferredType || '').toLowerCase();

      // If chart_data exists and is one of our supported Kendo types, render it.
      if (chartData && (chartType === 'pie' || chartType === 'bar' || chartType === 'line')) {
        return <SimpleKendoChart chartData={chartData} />;
      }

      let img = content?.attachemnt?.content || content?.attachment?.content;
      let imgType =
        content?.attachemnt?.type || content?.attachment?.type || 'image/png';

      // Fallback: extract from payload.execution_steps[].result if not flattened yet
      if (!img && Array.isArray(content?.payload?.execution_steps)) {
        const stepWithImg = content.payload.execution_steps.find(
          s =>
            s?.result?.attachemnt?.content ||
            s?.result?.attachment?.content ||
            s?.result?.image?.content
        );
        if (stepWithImg?.result) {
          const r = stepWithImg.result;
          img = r?.attachemnt?.content || r?.attachment?.content || r?.image?.content || img;
          imgType = r?.attachemnt?.type || r?.attachment?.type || r?.image?.type || imgType;
        }
      }

      if (img) {
        return <SimpleChartImage imageContent={img} imageType={imgType} />;
      }
      {
        // For chart tiles without data/image yet, show a centered loading
        // message with the shared tile-loading-message style so it matches
        // grid tiles.
        const msg = content?.message || 'Loading';
        const isLoading =
          typeof msg === 'string' && msg.toLowerCase().includes('loading');
        if (isLoading) {
          return <div className="tile-loading-message">{msg}</div>;
        }
        return <div className="k-text-secondary">{msg}</div>;
      }
    }
    {
      // Generic tiles: if they are in a loading state, also use the shared
      // centered loading style; otherwise fall back to a simple text block.
      const msg = content?.message || data?.Description || 'Tile';
      const isLoading =
        typeof msg === 'string' && msg.toLowerCase().includes('loading');
      if (isLoading) {
        return <div className="tile-loading-message">{msg}</div>;
      }
      return <div className="k-text-secondary">{msg}</div>;
    }
  };

  // Helper: derive the *visual* content type for a tile so layout classes
  // (chart vs grid) stay in sync with renderTileContent. This also treats
  // backend transitional types like "Mid" as Chart tiles.
  const getTileVisualType = useCallback(
    (tile) => {
      console.log(tile);
      const raw = tile?.data?.Type;
      const lower = String(raw || '').toLowerCase();
      if (lower === 'mid') return 'Chart';
      return raw || '';
    },
    [tileContents]
  );

  const tileLayoutItems = useMemo(
    () =>
      tiles.map((it, idx) => ({
        // Prefer current rendered content type so we can tune layout (e.g., overflow) per tile.
        __contentType: getTileVisualType(it),
        id: it.id,
        order: it.order ?? idx,
        resizable: editMode ? true : false,
        reorderable: editMode,
        header: (
          <TileHeader
            title={it?.data?.Name}
            subtitle={it?.data?.Description}
            tile={it}
            onRefresh={handleRefreshTile}
            onFullscreen={handleOpenFullscreen}
            onDelete={handleDeleteTile}
            onEdit={handleOpenEdit}
            disabled={deletingTileIds.has(`${it.id}`) || refreshingTileIds.has(`${it.id}`)}
          />
        ),
        body: (
          <div
            className={`k-p-2 tile-body ${getTileVisualType(it) === 'Chart' ? 'tile-body--chart' : 'tile-body--grid'}`}
            style={{ position: 'relative' }}
          >
            {renderTileContent(it)}
            {(deletingTileIds.has(`${it.id}`) || refreshingTileIds.has(`${it.id}`)) && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(255,255,255,0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                  backdropFilter: 'blur(1px)'
                }}
              >
                <div className="k-d-flex k-flex-col k-align-items-center k-gap-2">
                  <Loader />
                  <span className="k-text-secondary" style={{ fontSize: 12 }}>
                    {deletingTileIds.has(`${it.id}`) ? 'Deleting…' : 'Refreshing…'}
                  </span>
                </div>
              </div>
            )}
          </div>
        )
      })),
    [tiles, tileContents, handleDeleteTile, handleOpenEdit, handleRefreshTile, editMode, deletingTileIds, refreshingTileIds]
  );

  return (
    <BaseTileLayout title="Smart Chat Dashboard">
      <Card
        className={`k-p-3 page smart-chat-dashboard ${editMode ? 'tilelayout-edit-mode' : ''}`}
      >
        <div className="k-card-header k-pb-3 k-d-flex k-align-items-center">
          <h5 className="k-m-0">Smart Dashboard</h5>
          <div className="k-ml-auto k-d-flex k-gap-1" style={{ gap: 4 }}>
            {/* Hide Refresh while editing to avoid confusing state changes */}
            {!editMode && (
              <Button
                size="small"
                fillMode="flat"
                onClick={() => fetchTiles()}
                title="Refresh"
                aria-label="Refresh"
                disabled={loading}
                rounded="full"
                style={{ minWidth: 20, width: 20, height: 20, padding: 0 }}
              >
                <span className="k-icon k-font-icon k-i-reload" style={{ fontSize: 12 }} />
              </Button>
            )}
            {/* Show Edit only when not editing and tiles exist */}
            {!editMode && tiles.length > 0 && (
              <Button
                size="small"
                fillMode="flat"
                themeColor="base"
                onClick={() => {
                  // take a snapshot to allow cancel without data reload/hide
                  preEditTilesRef.current = tiles;
                  setEditMode(true);
                  setLayoutDirty(false);
                }}
                title="Edit layout"
                aria-label="Edit layout"
                rounded="full"
                style={{ minWidth: 20, width: 20, height: 20, padding: 0 }}
              >
                <span className="k-icon k-font-icon k-i-pencil" style={{ fontSize: 12 }} />
              </Button>
            )}
            {editMode && (
              <>
                <Button
                  size="small"
                  themeColor="primary"
                  disabled={!layoutDirty}
                  onClick={async () => {
                    const ok = await persistNow(tiles);
                    if (ok) {
                      setLayoutDirty(false);
                      setEditMode(false);
                    }
                  }}
                >
                  Save Layout
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    // Restore pre-edit snapshot without triggering a full reload/loader
                    if (preEditTilesRef.current) {
                      setTiles(preEditTilesRef.current);
                      preEditTilesRef.current = null;
                    }
                    setEditMode(false);
                    setLayoutDirty(false);
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        </div>
        {editMode && (
          <div
            className="k-mb-3"
            style={{
              padding: '8px 12px',
              background: '#eef6ff',
              color: '#0a59a0',
              borderRadius: 4
            }}
          >
            Editing layout… Drag/resize tiles. Click <strong>Save</strong> to
            persist or <strong>Cancel</strong> to discard.
          </div>
        )}
        {loading ? (
          <div className="k-p-3" ref={setTileLayoutEl}>
            {(() => {
              // Make skeleton responsive to available width (avoid squeezed look in overlay)
              // If the layout container width is large, prefer 3 columns; otherwise fall back
              // to the measured layoutColumns (1/2/3). This keeps skeleton from sticking to
              // a single thin column in wide containers.
              let skColumns = Math.max(1, Math.min(3, Number(layoutColumns) || 3));
              try {
                const rect = tileLayoutEl?.getBoundingClientRect?.();
                const w = rect?.width || window.innerWidth || 1200;
                if (w >= 900) skColumns = 3; else if (w >= 600) skColumns = Math.max(2, skColumns);
              } catch (_) {}
              const count = skColumns * 3; // ~3 rows worth regardless of width
              const skItems = Array.from({ length: count }, (_, i) => ({
                id: `sk_${i}`,
                order: i,
                resizable: false,
                reorderable: false,
                colSpan: 1,
                rowSpan: 3,
                header: (
                  <div className="tile-skeleton-header">
                    <div className="skeleton-rect skeleton-title" />
                    <div className="skeleton-rect skeleton-subtitle" />
                  </div>
                ),
                body: (
                  <div className="tile-skeleton-body">
                    <div className="skeleton-rect skeleton-block" />
                    <div className="skeleton-rect skeleton-line" />
                    <div className="skeleton-rect skeleton-line" />
                  </div>
                )
              }));

              // Simple positions: 3 per row
              let curCol = 1;
              let curRow = 1;
              const positions = skItems.map(it => {
                if (curCol > skColumns) {
                  curCol = 1;
                  curRow += it.rowSpan || 1;
                }
                const pos = { col: curCol, row: curRow, colSpan: 1, rowSpan: it.rowSpan || 1 };
                curCol += 1;
                return pos;
              });

              return (
                <TileLayout
                  dataItemKey="id"
                  columns={skColumns}
                  rowHeight={78}
                  gap={{ rows: 12, columns: 12 }}
                  autoFlow="column dense"
                  items={skItems}
                  positions={positions}
                  reorderable={false}
                  resizable={false}
                />
              );
            })()}
          </div>
        ) : tiles.length === 0 ? (
          <div
            className="k-d-flex k-justify-content-center k-align-items-center"
            style={{ height: 300 }}
          >
            <Card className="k-p-6 k-text-center">
              <div className="k-text-secondary k-mb-2">No tiles yet</div>
              <div className="k-mb-3">
                Add tiles from chat using the bookmark icon.
              </div>
              <div className="k-text-secondary k-text-small">
                After adding, they’ll appear here automatically.
              </div>
            </Card>
          </div>
        ) : (
          <div className="k-p-3" ref={setTileLayoutEl}>
            <TileLayout
              dataItemKey="id"
              columns={layoutColumns}
              rowHeight={78}
              gap={{ rows: 12, columns: 12 }}
              /* Row-dense behavior more closely matches Kendo dashboard demos */
              autoFlow="row dense"
              items={tileLayoutItems}
              // In controlled mode, TileLayout relies on `col` (required) and `row` (recommended)
              // to properly snap and rearrange items.
              // Keep the positions shape aligned with `TilePosition` (no custom id/order fields).
              positions={tiles.map(t => ({
                col: Number.isFinite(+t.col) ? +t.col : 1,
                row: Number.isFinite(+t.row) ? +t.row : 1,
                // Render spans clamped to viewport columns to prevent squeeze/overflow
                colSpan: Math.min(
                  layoutColumns,
                  Number.isFinite(+t.colSpan) ? +t.colSpan : 1
                ),
                rowSpan: Number.isFinite(+t.rowSpan) ? +t.rowSpan : 1
              }))}
              // Ensure interactions enabled globally; items also set these as true
              reorderable={editMode}
              // Explicitly allow both directions (Kendo supports: 'horizontal' | 'vertical' | boolean)
              resizable={editMode ? true : false}
              onReposition={onReposition}
              onResize={onResize}
            />
          </div>
        )}

        {editDialogOpen && (
          <Dialog
            title="Edit Tile"
            onClose={() => setEditDialogOpen(false)}
            minWidth={480}
            width={520}
          >
            <div className="k-form">
              <div className="k-form-field k-mb-3">
                <label className="k-label k-mb-1 k-required">Title</label>
                <Input
                  value={editContext?.name || ''}
                  onChange={e =>
                    setEditContext(prev => ({ ...prev, name: e?.value ?? '' }))
                  }
                  placeholder="Enter tile title"
                />
              </div>
              <div className="k-form-field">
                <label className="k-label k-mb-1 k-required">Description</label>
                <TextArea
                  rows={3}
                  value={editContext?.desc || ''}
                  onChange={e =>
                    setEditContext(prev => ({ ...prev, desc: e?.value ?? '' }))
                  }
                  placeholder="Enter tile description"
                />
              </div>
            </div>
            <DialogActionsBar>
              <Button
                size="medium"
                fillMode="solid"
                onClick={() => setEditDialogOpen(false)}
                disabled={savingEdit}
              >
                Cancel
              </Button>
              <Button
                size="medium"
                themeColor="primary"
                fillMode="solid"
                onClick={async () => {
                  if (!editContext) return;
                  try {
                    setSavingEdit(true);
                    if (
                      !editContext.name?.trim() ||
                      !editContext.desc?.trim()
                    ) {
                      GenerateNotification(
                        buildNotification({
                          title: 'Required',
                          description: 'Title and Description are required.',
                          style: 'warning'
                        }),
                        NOTIFICATION_TYPES.APP,
                        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
                      );
                      setSavingEdit(false);
                      return;
                    }
                    const resp = await editTile(
                      editContext.tile.data.TileId,
                      {
                        Name: editContext.name,
                        Description: editContext.desc
                      },
                      editContext.tile.data.SmartAppKey || primaryAppKey
                    );
                    if (resp?.isSuccess) {
                      setTiles(prev =>
                        prev.map(p =>
                          p.id === editContext.tile.id
                            ? {
                                ...p,
                                data: {
                                  ...p.data,
                                  Name: editContext.name,
                                  Description: editContext.desc
                                }
                              }
                            : p
                        )
                      );
                      setEditDialogOpen(false);
                    } else {
                      GenerateNotification(
                        buildNotification({
                          title: 'Edit failed',
                          description:
                            resp?.message || 'Unable to save changes',
                          style: 'error'
                        }),
                        NOTIFICATION_TYPES.APP,
                        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
                      );
                    }
                  } catch (e) {
                    GenerateNotification(
                      buildNotification({
                        title: 'Error',
                        description: e.message,
                        style: 'error'
                      }),
                      NOTIFICATION_TYPES.APP,
                      EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
                    );
                  } finally {
                    setSavingEdit(false);
                  }
                }}
                disabled={savingEdit}
              >
                {savingEdit ? 'Saving…' : 'Save'}
              </Button>
            </DialogActionsBar>
          </Dialog>
        )}
      </Card>
      {fullscreenCtx && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          onClick={(e) => {
            // close when clicking backdrop only
            if (e.target === e.currentTarget) handleCloseFullscreen();
          }}
        >
          <div style={{ background: '#fff', borderRadius: 8, width: '95vw', height: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="k-d-flex k-align-items-center" style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{fullscreenCtx?.tile?.data?.Name || 'Tile'}</div>
              <div className="k-ml-auto">
                <Button size="small" fillMode="flat" onClick={handleCloseFullscreen} title="Close" aria-label="Close" rounded="full" style={{ minWidth: 24, width: 24, height: 24, padding: 0 }}>
                  <span className="k-icon k-font-icon k-i-close" />
                </Button>
              </div>
            </div>
            <div style={{ flex: '1 1 auto', minHeight: 0, padding: 12, overflow: 'auto' }}>
              {(() => {
                const data = fullscreenCtx?.tile?.data;
                const content = fullscreenCtx?.content;
                const type = content?.type || data?.Type;
                if (type === 'Grid') {
                  // Rebuild metadata/values similar to renderTileContent but larger
      let md = content?.payload?.metadata || content?.Metadata;
      let vals = content?.payload?.values || content?.Values;
                  if ((!Array.isArray(md) || md.length === 0) && Array.isArray(content?.payload?.execution_steps)) {
                    const stepWithResult = content.payload.execution_steps.find(s => s?.result && (s.result.metadata || s.result.values));
                    if (stepWithResult?.result) {
                      md = stepWithResult.result.metadata || [];
                      vals = stepWithResult.result.values;
                    }
                  }
                  md = Array.isArray(md) ? md : [];
                  const normalized = normalizeGridValues(md, vals);
                  // Fill fullscreen height similar to the chat grid fullscreen behavior.
                  return <SimpleGrid metadata={md} values={normalized} height={'fill'} pageSize={20} />;
                }
                if (type === 'Chart') {
                  const chartData = content?.payload?.chart_data || content?.payload?.chartData || content?.chart_data || null;
                  const inferredType = chartData?.lines?.[0]?.type || chartData?.Lines?.[0]?.Type;
                  const chartType = String(chartData?.type || inferredType || '').toLowerCase();
                  if (chartData && (chartType === 'pie' || chartType === 'bar' || chartType === 'line')) {
                    return <SimpleKendoChart chartData={chartData} height={520} />;
                  }
                  let img = content?.attachemnt?.content || content?.attachment?.content;
                  let imgType = content?.attachemnt?.type || content?.attachment?.type || 'image/png';
                  if (!img && Array.isArray(content?.payload?.execution_steps)) {
                    const stepWithImg = content.payload.execution_steps.find(s => s?.result?.attachemnt?.content || s?.result?.attachment?.content || s?.result?.image?.content);
                    if (stepWithImg?.result) {
                      const r = stepWithImg.result;
                      img = r?.attachemnt?.content || r?.attachment?.content || r?.image?.content || img;
                      imgType = r?.attachemnt?.type || r?.attachment?.type || r?.image?.type || imgType;
                    }
                  }
                  if (img) return <SimpleChartImage imageContent={img} imageType={imgType} />;
                  return <div>{content?.message || 'Chart'}</div>;
                }
                return <div>{content?.message || data?.Description || 'Tile'}</div>;
              })()}
            </div>
          </div>
        </div>
      )}
    </BaseTileLayout>
  );
};

export default SmartChatDashboard;
